import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { Questionnaire } from 'src/app/shared/models/questionnaire.model';
import { PreviewQuestionnaireComponent } from './preview-questionnaire/preview-questionnaire.component';
import { Project } from 'src/app/shared/models/project.model';
import { ProjectService } from 'src/app/services/project.service';
import { InviteExamineeComponent } from 'src/app/shared/components/invite-examinee/invite-examinee.component';

@Component({
    selector: 'app-project-questionnaire',
    templateUrl: './project-questionnaire.component.html',
    styleUrl: './project-questionnaire.component.scss',
    standalone: false
})
export class ProjectQuestionnaireComponent implements OnInit {
	@ViewChild('deleteQuestionnaireModal') deleteQuestionnaireModal!: TemplateRef<any>;
	@ViewChild('cloneQuestionnaireModal') cloneQuestionnaireModal!: TemplateRef<any>;
	@ViewChild('previewQuestionnaireModal') previewQuestionnaireModal!: PreviewQuestionnaireComponent;
	@ViewChild('inviteExamineeModal') inviteExamineeModal!: InviteExamineeComponent;

	@ViewChild('successModal') successModal!: TemplateRef<any>;

	private refreshSubscription!: Subscription;
	deleteQuestionnaireModalRef: any;
	cloneQuestionnaireModalRef: any;
	selectedQuestionnaire!: Questionnaire;

	submitted = false;
	spinner = false;
	success = false;
	successMessage = '';
	message = '';

	@Input() currentProject!: any;
	private searchSubject = new Subject<string>();

	projects: Project[] = [];

	questionnaires: Questionnaire[] = [];
  currentPage: number = 1;
  pageSize: number = 8;
  totalQuestionnaires: number = 0;
  searchText: string = '';
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';

	listStatus: any[] = [
		{ name: 'DRAFT', status_color: 'info'},
		{ name: 'ACTIVE', status_color: 'success'},
		{ name: 'PAUSED', status_color: 'warning'},
		{ name: 'CANCELED', status_color: 'danger'}
	];

	startIndex: number = 0;
  endIndex: number = 0;

	loading = true;
	modalloading = false;
	listloading = true;

	currentUser: any = null;
	current_client: any = null;

	cloned_questionnaire_id!: number;
	cloned_project_id!: number | undefined;
	cloned_questionnaire_name = '';

	isAllow = false;
	isOwner = false;
	isEditor = false;
	isAnalyst = false;
	allowedRoles = ['OWNER', 'EDITOR'];


	constructor(private questionnaireService: QuestionnaireService, private authService: AuthService, private modalService: NgbModal, private projectService: ProjectService) {}

	ngOnInit(): void {
		this.currentUser = this.authService.currentUserValue;
		this.current_client = this.authService.currentClientValue;

		this.isOwner = this.current_client.roles.includes('OWNER');
		this.isEditor = this.current_client.roles.includes('EDITOR');
		this.isAnalyst = this.current_client.roles.includes('ANALYST');
		this.isAllow = this.current_client.roles.some((role: string) => this.allowedRoles.includes(role));

		this.isAllow = this.authService.currentClientValue.roles.some((role: string) => this.allowedRoles.includes(role));

		this.getQuestionnaires();

		this.refreshSubscription = this.questionnaireService.getRefreshObservable().subscribe(() => {
      this.getQuestionnaires();
    });

    // Subscribe to search input changes with debounce
    this.searchSubject.pipe(
      debounceTime(300), // Adjust debounce time as needed
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1; // Reset to first page when search text changes
      this.getQuestionnaires();
    });
	}

	ngOnDestroy() {
    this.refreshSubscription.unsubscribe();
  }

	getStatusColor(status: string) {
		const s = this.listStatus.find(item => item.name === status);
		return s.status_color;
	}

	previewQuestionnaire(questionnaireID: number) {
		this.previewQuestionnaireModal.openModal(this.currentProject.id, questionnaireID);
	}

	openCloneQuestionnaireModal(questionnaireID: number) {
		this.cloneQuestionnaireModalRef = this.modalService.open(this.cloneQuestionnaireModal, { size: 'l', backdrop: 'static', keyboard : false });
		this.listloading = true;
		this.cloned_questionnaire_id = questionnaireID;

		this.projectService.getProjectsForAssign().subscribe({
			next: (response: any) => {
				this.projects = response;
				this.cloned_project_id = this.projects.find(item => item.id === this.currentProject.id)?.id;
				this.listloading = false;
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Projects');
			}
		});
	}

	cloneQuestionnaire() {
		this.submitted = true;

		if (!this.cloned_questionnaire_name || this.cloned_questionnaire_name === '') {
			return;
		}

		this.spinner = true;

		const data = {
			cloned_questionnaire_name: this.cloned_questionnaire_name,
			cloned_questionnaire_id: this.cloned_questionnaire_id,
			cloned_project_id: this.cloned_project_id,
		};

		this.questionnaireService.cloneQuestionnaire(data).subscribe({
			next: () => {
				this.submitted = false;
				this.spinner = false;
				this.success = true;
				this.successMessage = 'Questionnaire cloned successfully.';

				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.submitted = false;
				this.spinner = false;
				this.success = false;
				this.successMessage = 'Questionnaire can not be cloned!';
				this.message = err.message ? err.message : err;

				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			}
		});
	}

	getQuestionnaires() {
		this.loading = true;
    const params = {
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString(),
      searchText: this.searchText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };

		this.questionnaireService.getProjectQuestionnaires(this.currentProject.id, params).subscribe({
			next: (response: any) => {
				this.questionnaires = response.questionnaires;
				this.totalQuestionnaires = response.totalQuestionnaires;
				this.calculateIndexes();
				this.loading = false;
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Project Questionnaires');
			}
		});
  }

	calculateIndexes() {
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalQuestionnaires);
  }

	onSearch() {
    this.searchSubject.next(this.searchText);
  }

	onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.getQuestionnaires();
  }

	openDeleteQuestionnaireModal(questionnaire: Questionnaire) {
		this.selectedQuestionnaire = questionnaire;

		this.deleteQuestionnaireModalRef = this.modalService.open(this.deleteQuestionnaireModal, { size: 'l', backdrop: 'static', keyboard : false });
	}

	deleteQuestionnaire() {
		this.submitted = true;
		this.spinner = true;

		this.questionnaireService.deleteProjectQuestionnaire(this.currentProject.id, this.selectedQuestionnaire.id).subscribe({
			next: () => {
				this.submitted = false;
				this.spinner = false;
				this.success = true;
				this.deleteQuestionnaireModalRef.close();
				this.successMessage = 'Questionnaire deleted successfully.';

				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.submitted = false;
				this.spinner = false;
				this.success = false;
				this.deleteQuestionnaireModalRef.close();
				this.successMessage = 'Questionnaire can not be deleted!';
				this.message = err.message ? err.message : err;

				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			}
		});
	}

	inviteExaminee(questionnaire: Questionnaire) {
		this.inviteExamineeModal.openModal(this.currentProject.id, questionnaire);
	}

	refreshQuestionnaires() {
		this.currentPage = 1;
		this.searchText = '';
		this.getQuestionnaires();
	}

	closeModal() {
		if (this.success) {
			this.refreshQuestionnaires();
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}
}
